import React, { useState } from "react";
import * as engineService from "@/shared/api/services/engineService";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Power, PowerOff, Building2,
  CheckCircle2, XCircle, Clock, Copy, Eye, EyeOff, Loader2, TrendingUp, RefreshCw,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useToast } from "@/shared/components/ui/use-toast";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "text-emerald-400", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.25)", label: "Active" },
  active: { icon: CheckCircle2, color: "text-emerald-400", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.25)", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "text-red-400", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Suspended" },
  suspended: { icon: XCircle, color: "text-red-400", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "text-amber-400", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.25)", label: "Inactive" },
  pending: { icon: Clock, color: "text-amber-400", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.25)", label: "Pending" },
};

const providers = [
  { code: "SCB", name: "ธนาคารไทยพาณิชย์", nameEn: "Siam Commercial Bank" },
  { code: "KBANK", name: "ธนาคารกสิกรไทย", nameEn: "Kasikorn Bank" },
  { code: "BBL", name: "ธนาคารกรุงเทพ", nameEn: "Bangkok Bank" },
  { code: "KTB", name: "ธนาคารกรุงไทย", nameEn: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "ทรูมันนี่ วอลเล็ท", nameEn: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "พร้อมเพย์ QR", nameEn: "PromptPay QR" },
];

/* ─── Stat Card ─────────────────────────────────────────────── */
function StatCard({ title, value, icon: Icon, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)}
      className="relative" style={{ perspective: 900 }}
    >
      <motion.div animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.06 : 0.85 }} transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1a, transparent 68%)`, filter: "blur(8px)" }}
      />
      <motion.div animate={{ y: hovered ? -6 : 0, rotateX: hovered ? -1.5 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
          border: `1px solid ${hovered ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 18px 50px rgba(0,0,0,0.5), 0 0 28px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />
        <motion.div animate={{ x: hovered ? "108%" : "-108%" }} transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}08, transparent 70%)` }} />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100">{value}</motion.p>
          </div>
          <motion.div animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${gradient})`, boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`, transition: "box-shadow 0.3s" }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineMerchants() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const engineId = searchParams.get("engineId");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState(null);
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const resellerName = "Gaming Partners Ltd";

  const { toast } = useToast();

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const { data: merchantsData = { data: [], total: 0, totalPages: 0 }, isLoading } = useQuery({
    queryKey: ["engine-merchants", page, limit, search, engineId],
    queryFn: () => engineId ? adminService.getMerchantWithEngineId(engineId, page, limit, search) : engineService.getMyMerchants(page, limit, search),
    keepPreviousData: true,
  });

  const merchants = merchantsData.data || [];
  const totalItems = merchantsData.total || 0;
  const totalPages = merchantsData.totalPages || 0;

  const createMutation = useMutation({
    mutationFn: (data) => engineService.createMerchant(data),
    onSuccess: () => { queryClient.invalidateQueries(["engine-merchants"]); },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => engineService.updateMerchant(id, data),
    onSuccess: () => { queryClient.invalidateQueries(["engine-merchants"]); },
  });

  const filteredMerchants = merchants; // Server-side searching already filtered these

  const handleSave = async (data) => {
    try {
      if (selectedMerchant) {
        await updateMutation.mutateAsync({ id: selectedMerchant.id, data });
        toast({
          title: t("success") || "Success",
          description: t("merchantUpdatedSuccess") || "Merchant updated successfully",
        });
      } else {
        await createMutation.mutateAsync(data);
        toast({
          title: t("success") || "Success",
          description: t("merchantCreatedSuccess") || "Merchant created successfully",
        });
      }
      setFormOpen(false);
      setSelectedMerchant(null);
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: t("error") || "Error",
        description: error?.response?.data?.message || error?.message || "Failed to save merchant",
        variant: "destructive",
      });
      // Throw error to keep the form's saving state active if needed, 
      // but handleSubmit already sets saving(false) after onSave
      throw error;
    }
  };

  const handleToggleStatus = async () => {
    if (!suspendDialog) return;
    const newStatus = suspendDialog.status?.toUpperCase() === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    try {
      await updateMutation.mutateAsync({
        id: suspendDialog.id,
        data: { name: suspendDialog.name, status: newStatus }
      });
      toast({
        title: t("statusUpdated") || "Status Updated",
        description: `Merchant status changed to ${newStatus.toLowerCase()}`,
      });
      setSuspendDialog(null);
    } catch (error) {
      toast({
        title: t("error") || "Error",
        description: error?.response?.data?.message || error?.message || "Failed to update status",
        variant: "destructive",
      });
    }
  };


  const totalRegistered = merchantsData.totalRegisteredMerchants || 0;
  const activeMerchantsTotal = merchantsData.totalActiveMerchants || 0;

  return (
    <EngineLayout currentPage="EngineMerchants" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))", border: "1px solid rgba(251,146,60,0.15)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(251,146,60,0.45), rgba(34,211,238,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(251,146,60,0.07), transparent 70%)" }} />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-orange-400 mb-1">{t("management")}</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("merchants")}</h1>
              <p className="text-sm text-slate-400"><span className="text-cyan-400 font-semibold">{totalRegistered}</span> {t("merchantsRegisteredCount")}</p>
            </div>
            <motion.button whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.96 }}
              onClick={() => { setSelectedMerchant(null); setFormOpen(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
              style={{ background: "linear-gradient(135deg, #fb923c, #ea580c)", boxShadow: "0 4px 20px rgba(251,146,60,0.38)", transition: "box-shadow 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 32px rgba(251,146,60,0.55)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(251,146,60,0.38)"}
            >
              <Plus className="h-4 w-4" />{t("addMerchant")}
            </motion.button>
          </div>
        </motion.div>


        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard title={t("totalMerchants")} value={totalRegistered} icon={Building2} gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0} />
          <StatCard title={t("activeMerchants")} value={activeMerchantsTotal} icon={CheckCircle2} gradient="#34d399, #059669" accent="#34d399" delay={0.05} />
          {/* <StatCard title={t("todaysVolume")} value="฿0" icon={TrendingUp} gradient="#fb923c, #ea580c" accent="#fb923c" delay={0.1} /> */}
        </div>

        {/* Search */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("searchMerchantsPlaceholder")}
            className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
            style={{ background: "rgba(15,23,42,0.7)", border: "1px solid rgba(148,163,184,0.12)", transition: "border-color 0.2s, box-shadow 0.2s" }}
            onFocus={e => { e.target.style.borderColor = "rgba(34,211,238,0.3)"; e.target.style.boxShadow = "0 0 0 3px rgba(34,211,238,0.07)"; }}
            onBlur={e => { e.target.style.borderColor = "rgba(148,163,184,0.12)"; e.target.style.boxShadow = "none"; }}
          />
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="relative rounded-2xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))", border: "1px solid rgba(148,163,184,0.09)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent)" }} />
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 580 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "rgba(2,6,23,0.4)" }}>
                  {[t("merchant"), t("code"), t("status"), t("settlement"), t("created"), ""].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <tr><td colSpan={6} className="text-center py-14">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                        <span className="text-sm">{t("loading")}</span>
                      </div>
                    </td></tr>
                  ) : filteredMerchants.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-14 text-slate-600 text-sm">{t("noMerchantsFound")}</td></tr>
                  ) : filteredMerchants.map((merchant, i) => {
                    const st = statusConfig[merchant.status?.toLowerCase()] || statusConfig.pending;
                    const StatusIcon = st.icon;
                    return (
                      <motion.tr key={merchant.id}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ delay: i * 0.03 }}
                        style={{ borderBottom: "1px solid rgba(148,163,184,0.05)", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <motion.div whileHover={{ scale: 1.1, rotate: 5 }}
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: "linear-gradient(135deg, rgba(251,146,60,0.2), rgba(234,88,12,0.1))", border: "1px solid rgba(251,146,60,0.2)", transition: "box-shadow 0.25s" }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 14px rgba(251,146,60,0.3)"}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                            >
                              <Building2 className="h-4 w-4 text-orange-400" />
                            </motion.div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-200 truncate">{merchant.name}</p>
                              <p className="text-xs text-slate-500 truncate">{merchant.contactEmail || merchant.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-xs text-slate-400 px-2.5 py-1 rounded-lg" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.1)" }}>
                            {merchant.code}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: st.bg, border: `1px solid ${st.border}` }}>
                            <StatusIcon className={`h-3 w-3 ${st.color}`} />
                            <span className={st.color}>{st.label}</span>
                          </div>
                        </td>
                        {/* Settlement */}
                        <td className="mc-td">
                          <span className="mc-text-sm">{merchant.settlementPeriod || "—"}</span>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-slate-500">{merchant.createdAt ? format(new Date(merchant.createdAt), "MMM d, yyyy") : "N/A"}</td>
                        <td className="px-4 py-3.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer outline-none"
                                style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.1)", transition: "background 0.18s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.12)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.06)"}
                              >
                                <MoreHorizontal className="h-4 w-4 text-slate-500" />
                              </motion.button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" style={{ background: "rgba(2,6,23,0.97)", border: "1px solid rgba(34,211,238,0.12)", borderRadius: 12, padding: 4, minWidth: 156 }}>
                              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedMerchant(merchant); setFormOpen(true); }} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800 text-slate-400">
                                <Pencil className="h-3.5 w-3.5 mr-2 text-cyan-400" />{t("edit")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator style={{ background: "rgba(34,211,238,0.07)", margin: "4px 0" }} />
                              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSuspendDialog(merchant); }} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800" style={{ color: merchant.status?.toUpperCase() === "ACTIVE" ? "#f87171" : "#34d399" }}>
                                {merchant.status?.toUpperCase() === "ACTIVE" ? <><PowerOff className="h-3.5 w-3.5 mr-2" />{t("suspend")}</> : <><Power className="h-3.5 w-3.5 mr-2" />{t("activate")}</>}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800/50 bg-slate-900/30">
              <div className="text-xs text-slate-500">
                {t("showing")} <span className="text-slate-300">{(page - 1) * limit + 1}</span> - <span className="text-slate-300">{Math.min(page * limit, totalItems)}</span> {t("of")} <span className="text-slate-300">{totalItems}</span>
              </div>
              <div className="flex items-center gap-2">
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </motion.button>
                <div className="flex items-center gap-1.5 mx-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${page === i + 1 ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/20" : "bg-slate-800/40 text-slate-500 hover:bg-slate-800"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <MerchantFormModal merchant={selectedMerchant} open={formOpen} onClose={() => { setFormOpen(false); setSelectedMerchant(null); }} onSave={handleSave} providers={providers} />

      <AlertDialog open={!!suspendDialog} onOpenChange={() => setSuspendDialog(null)}>
        <AlertDialogContent style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(34,211,238,0.14)", borderRadius: 16 }}>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.35), transparent)" }} />
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">{suspendDialog?.status?.toUpperCase() === "ACTIVE" ? t("suspendMerchantTitle") : t("activateMerchantTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {suspendDialog?.status?.toUpperCase() === "ACTIVE" ? `${t("suspendMerchantConfirm")} "${suspendDialog?.name}"? ${t("willNotProcessTransactions")}` : `${t("activateMerchantConfirm")} "${suspendDialog?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.14)", color: "#94a3b8", borderRadius: 10 }}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus}
              style={{ borderRadius: 10, border: "none", fontWeight: 700, cursor: "pointer", background: suspendDialog?.status?.toUpperCase() === "ACTIVE" ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, #34d399, #059669)", color: "#fff" }}
            >
              {suspendDialog?.status?.toUpperCase() === "ACTIVE" ? t("suspend") : t("activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EngineLayout>
  );
}

function MerchantFormModal({ merchant, open, onClose, onSave, providers }) {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    code: "",
    email: "",
    password: "",
    callback_url: "",
    webhook_url: "",
    deposit_enabled: true,
    withdrawal_enabled: true,
    min_deposit: 100,
    max_deposit: 1000000,
    min_withdrawal: 100,
    max_withdrawal: 500000,
    daily_deposit_limit: 10000000,
    daily_withdrawal_limit: 5000000,
    deposit_fee: "", withdrawal_fee: "",
    settlement_period: "T+1 (Next Day)",
    notes: "",
    api_key: "",
    secret_key: "",
    ip_whitelist: [],
    rate_limit: 100,
    contact_name: "",
    contact_email: "",
    contact_phone: ""
  });
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ipInput, setIpInput] = useState("");
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) setErrors({});
    if (merchant) {
      setForm({
        ...merchant,
        min_deposit: merchant.minDeposit ?? merchant.min_deposit ?? 100,
        max_deposit: merchant.maxDeposit ?? merchant.max_deposit ?? 1000000,
        min_withdrawal: merchant.minWithdrawal ?? merchant.min_withdrawal ?? 100,
        max_withdrawal: merchant.maxWithdrawal ?? merchant.max_withdrawal ?? 500000,
        daily_deposit_limit: merchant.dailyDepositLimit ?? merchant.daily_deposit_limit ?? 10000000,
        daily_withdrawal_limit: merchant.dailyWithdrawalLimit ?? merchant.daily_withdrawal_limit ?? 5000000,
        deposit_fee: merchant.depositFee ?? merchant.deposit_fee ?? "",
        withdrawal_fee: merchant.withdrawalFee ?? merchant.withdrawal_fee ?? "",
        settlement_period: merchant.settlementPeriod || merchant.settlement_period || "T+1 (Next Day)",
        notes: merchant.notes || "",
        api_key: merchant.apiKey || merchant.api_key || "",
        secret_key: merchant.secretKey || merchant.secret_key || "",
        ip_whitelist: Array.isArray(merchant.ipWhitelist) ? merchant.ipWhitelist : Array.isArray(merchant.ip_whitelist) ? merchant.ip_whitelist : [],
        rate_limit: merchant.rateLimit ?? merchant.rate_limit ?? 100,
        deposit_enabled: merchant.depositEnabled ?? merchant.deposit_enabled ?? true,
        withdrawal_enabled: merchant.withdrawalEnabled ?? merchant.withdrawal_enabled ?? true,
        callback_url: merchant.callbackUrl ?? merchant.callback_url ?? "",
        webhook_url: merchant.webhookUrl ?? merchant.webhook_url ?? "",
        contact_name: merchant.contactName ?? merchant.contact_name ?? "",
        contact_email: merchant.contactEmail ?? merchant.contact_email ?? "",
        contact_phone: merchant.contactPhone ?? merchant.contact_phone ?? ""
      });
    } else {
      setForm({
        name: "",
        code: "",
        email: "",
        password: "",
        callback_url: "",
        webhook_url: "",
        deposit_enabled: true,
        withdrawal_enabled: true,
        min_deposit: 100,
        max_deposit: 1000000,
        min_withdrawal: 100, max_withdrawal: 500000,
        daily_deposit_limit: 10000000, daily_withdrawal_limit: 5000000,
        deposit_fee: "", withdrawal_fee: "",
        settlement_period: "T+1 (Next Day)",
        notes: "",
        api_key: "pk_live_" + Array.from({ length: 32 }, () => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]).join(""),
        secret_key: "sk_live_" + Array.from({ length: 40 }, () => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]).join(""),
        ip_whitelist: [],
        rate_limit: 100,
        contact_name: "",
        contact_email: "",
        contact_phone: ""
      });
    }
  }, [merchant, open]);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.name?.trim()) newErrors.name = true;
    if (!form.code?.trim()) newErrors.code = true;
    if (!form.email?.trim()) newErrors.email = true;
    if (!merchant && !form.password?.trim()) newErrors.password = true;
    if (!form.api_key?.trim()) newErrors.api_key = true;
    if (!form.secret_key?.trim()) newErrors.secret_key = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.keys(newErrors)[0];
      toast({
        title: t("validationError") || "Validation Error",
        description: `${t("pleaseFillRequiredFields") || "Please fill all required fields"}: ${firstError.replace("_", " ")}`,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const payload = {
      name: form.name,
      code: form.code,
      email: form.email,
      password: form.password,
      apiKey: form.api_key,
      secretKey: form.secret_key,
      callbackUrl: form.callback_url?.trim() || null,
      webhookUrl: form.webhook_url?.trim() || null,
      depositEnabled: form.deposit_enabled,
      withdrawalEnabled: form.withdrawal_enabled,
      notes: form.notes?.trim() || null,
      ipWhitelist: form.ip_whitelist,
      rateLimit: form.rate_limit,
      minDeposit: form.min_deposit,
      maxDeposit: form.max_deposit,
      minWithdrawal: form.min_withdrawal,
      maxWithdrawal: form.max_withdrawal,
      dailyDepositLimit: form.daily_deposit_limit,
      dailyWithdrawalLimit: form.daily_withdrawal_limit,
      settlementPeriod: form.settlement_period,
      contactName: form.contact_name?.trim() || null,
      contactEmail: form.contact_email?.trim() || null,
      contactPhone: form.contact_phone?.trim() || null,
      depositFee: form.deposit_fee ? parseFloat(form.deposit_fee) : null,
      withdrawalFee: form.withdrawal_fee ? parseFloat(form.withdrawal_fee) : null,
    };
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const copyKey = () => { if (form.api_key) { navigator.clipboard.writeText(form.api_key); setCopiedKey(true); setTimeout(() => setCopiedKey(false), 2000); } };
  const copySecret = () => { if (form.secret_key) { navigator.clipboard.writeText(form.secret_key); setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 2000); } };

  const regenerateKeys = () => {
    setForm(p => ({
      ...p,
      api_key: "pk_live_" + Array.from({ length: 32 }, () => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]).join(""),
      secret_key: "sk_live_" + Array.from({ length: 40 }, () => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]).join("")
    }));
  };

  const addIp = () => {
    if (!ipInput.trim()) return;
    if (form.ip_whitelist.includes(ipInput.trim())) {
      setIpInput("");
      return;
    }
    setForm(p => ({ ...p, ip_whitelist: [...p.ip_whitelist, ipInput.trim()] }));
    setIpInput("");
  };

  const removeIp = (ip) => {
    setForm(p => ({ ...p, ip_whitelist: p.ip_whitelist.filter(x => x !== ip) }));
  };



  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-xl max-h-[90vh] overflow-y-auto" style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(34,211,238,0.14)", borderRadius: 20 }}>
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(251,146,60,0.45), rgba(34,211,238,0.3), transparent)" }} />
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-lg">{merchant ? t("editMerchant") : t("createNewMerchant")}</DialogTitle>
          <DialogDescription className="sr-only">
            {merchant ? "Update merchant details and configuration." : "Provide merchant information to create a new account."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <FField label={`${t("businessNameLabel")} *`}>
              <input
                value={form.name}
                onChange={e => {
                  setForm(p => ({ ...p, name: e.target.value }));
                  if (errors.name) setErrors(p => ({ ...p, name: false }));
                }}
                placeholder={t("merchantNamePlaceholder")}
                style={mkInp(errors.name ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                onFocus={focusInp} onBlur={blurInp}
              />
            </FField>
            <FField label={`${t("merchantCode")} *`}>
              <input
                value={form.code}
                onChange={e => {
                  setForm(p => ({ ...p, code: e.target.value.toUpperCase() }));
                  if (errors.code) setErrors(p => ({ ...p, code: false }));
                }}
                placeholder={t("merchantCodePlaceholder")}
                style={mkInp(errors.code ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                onFocus={focusInp} onBlur={blurInp}
              />
            </FField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FField label={`${t("emailLabel")} *`}>
              <input
                type="email"
                value={form.email}
                onChange={e => {
                  setForm(p => ({ ...p, email: e.target.value }));
                  if (errors.email) setErrors(p => ({ ...p, email: false }));
                }}
                placeholder={t("emailPlaceholder")}
                style={mkInp(errors.email ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                onFocus={focusInp} onBlur={blurInp}
              />
            </FField>
            <FField label={`${t("password")} ${merchant ? "" : "*"}`}>
              <input
                type="password"
                value={form.password}
                onChange={e => {
                  setForm(p => ({ ...p, password: e.target.value }));
                  if (errors.password) setErrors(p => ({ ...p, password: false }));
                }}
                placeholder={merchant ? t("leaveBlankToKeepPassword") : t("passwordPlaceholder")}
                style={mkInp(errors.password ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                onFocus={focusInp} onBlur={blurInp}
              />
            </FField>
          </div>

          <FField label="NOTES">
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." style={{ ...mkInp(), minHeight: 60, resize: "vertical" }} onFocus={focusInp} onBlur={blurInp} />
          </FField>

          <div className="space-y-4 pt-2">
            <FField label={`${t("apiKeyPublic")} *`}>
              <div className="flex gap-2">
                <input
                  type={showKey ? "text" : "password"}
                  value={form.api_key}
                  onChange={e => {
                    setForm(p => ({ ...p, api_key: e.target.value }));
                    if (errors.api_key) setErrors(p => ({ ...p, api_key: false }));
                  }}
                  placeholder={t("apisettingsapiKeyPlaceholder")}
                  style={mkInp(errors.api_key ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                  onFocus={focusInp} onBlur={blurInp}
                />
                <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }} type="button" onClick={() => setShowKey(!showKey)}
                  className="w-[42px] h-[42px] rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                  style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                >
                  {showKey ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </motion.button>
                <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }} type="button" onClick={copyKey}
                  className="w-[42px] h-[42px] rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                  style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                >
                  {copiedKey ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-slate-400" />}
                </motion.button>
              </div>
            </FField>

            <FField label={`${t("secretKey")} *`}>
              <div className="flex gap-2">
                <input
                  type={showSecret ? "text" : "password"}
                  value={form.secret_key}
                  onChange={e => {
                    setForm(p => ({ ...p, secret_key: e.target.value }));
                    if (errors.secret_key) setErrors(p => ({ ...p, secret_key: false }));
                  }}
                  placeholder={t("apisettingssecretKeyPlaceholder")}
                  style={mkInp(errors.secret_key ? { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)" } : {})}
                  onFocus={focusInp} onBlur={blurInp}
                />
                <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }} type="button" onClick={() => setShowSecret(!showSecret)}
                  className="w-[42px] h-[42px] rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                  style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                >
                  {showSecret ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </motion.button>
                <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }} type="button" onClick={regenerateKeys}
                  className="w-[42px] h-[42px] rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                  style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                >
                  <RefreshCw className="h-4 w-4 text-slate-400" />
                </motion.button>
              </div>
            </FField>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              { label: "MIN DEPOSIT (THB)", key: "min_deposit" },
              { label: "MAX DEPOSIT (THB)", key: "max_deposit" },
              { label: "MIN WITHDRAWAL (THB)", key: "min_withdrawal" },
              { label: "MAX WITHDRAWAL (THB)", key: "max_withdrawal" },
              { label: "DAILY DEPOSIT LIMIT (THB)", key: "daily_deposit_limit" },
              { label: "DAILY WITHDRAWAL LIMIT (THB)", key: "daily_withdrawal_limit" },
              { label: "DEPOSIT FEE (฿)", key: "deposit_fee" },
              { label: "WITHDRAWAL FEE (฿)", key: "withdrawal_fee" },
            ].map(f => (
              <FField key={f.key} label={f.label}>
                <input type="number" step="any" value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value ? parseFloat(e.target.value) : "" }))} style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
              </FField>
            ))}
          </div>

          <FField label="SETTLEMENT PERIOD">
            <div className="relative">
              <select value={form.settlement_period} onChange={e => setForm(p => ({ ...p, settlement_period: e.target.value }))} style={{ ...mkInp(), appearance: "none" }} onFocus={focusInp} onBlur={blurInp}>
                <option value="T+1 (Next Day)">T+1 (Next Day)</option>
                <option value="T+2">T+2</option>
                <option value="T+3">T+3</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
              </div>
            </div>
          </FField>

          <FField label="CALLBACK URL">
            <input value={form.callback_url} onChange={e => setForm(p => ({ ...p, callback_url: e.target.value }))} placeholder="https://your-site.com/api/callback" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
          </FField>

          <FField label="WEBHOOK URL (OPTIONAL)">
            <input value={form.webhook_url} onChange={e => setForm(p => ({ ...p, webhook_url: e.target.value }))} placeholder="https://your-site.com/api/webhook" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
          </FField>

          <FField label="IP WHITELIST">
            <div className="flex gap-2">
              <input
                value={ipInput}
                onChange={e => setIpInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIp(); } }}
                placeholder="e.g., 203.0.113.1"
                style={mkInp()}
                onFocus={focusInp}
                onBlur={blurInp}
              />
              <button
                type="button"
                onClick={addIp}
                className="flex-shrink-0 w-[42px] h-[42px] rounded-lg flex items-center justify-center text-white cursor-pointer outline-none border-0"
                style={{ background: "#0ea5e9" }}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.ip_whitelist.map(ip => (
                <div key={ip} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs text-slate-300">
                  {ip}
                  <button type="button" onClick={() => removeIp(ip)} className="text-slate-500 hover:text-red-400 p-0.5">
                    <XCircle className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </FField>

          <FField label="RATE LIMIT (REQUESTS/MINUTE)">
            <input type="number" value={form.rate_limit} onChange={e => setForm(p => ({ ...p, rate_limit: e.target.value ? parseInt(e.target.value, 10) : "" }))} placeholder="100" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
          </FField>

          <div className="space-y-4 pt-2">
            <FField label="CONTACT NAME">
              <input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="John Doe" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
            </FField>

            <FField label="CONTACT EMAIL">
              <input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="john@example.com" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
            </FField>

            <FField label="CONTACT PHONE">
              <input value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="+66 XXX XXX XXXX" style={mkInp()} onFocus={focusInp} onBlur={blurInp} />
            </FField>
          </div>

          {/* Toggles */}
          <div className="flex gap-5 pt-2">
            {[{ key: "deposit_enabled", label: t("deposits") || "Deposits" }, { key: "withdrawal_enabled", label: t("withdrawals") || "Withdrawals" }].map(tog => (
              <label key={tog.key} className="flex items-center gap-2 cursor-pointer">
                <motion.div onClick={() => setForm(p => ({ ...p, [tog.key]: !p[tog.key] }))} whileTap={{ scale: 0.95 }} className="relative flex-shrink-0"
                  style={{ width: 38, height: 21, borderRadius: 11, background: form[tog.key] ? "linear-gradient(135deg, #22d3ee, #0284c7)" : "rgba(34,211,238,0.08)", border: `1px solid ${form[tog.key] ? "rgba(34,211,238,0.35)" : "rgba(34,211,238,0.12)"}`, transition: "all 0.3s" }}
                >
                  <motion.div animate={{ left: form[tog.key] ? 18 : 2 }} transition={{ type: "spring", stiffness: 380, damping: 24 }}
                    style={{ position: "absolute", top: 2, width: 15, height: 15, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.25)" }}
                  />
                </motion.div>
                <span className="text-xs font-semibold text-slate-400">{tog.label}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-5 gap-2">
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-400 cursor-pointer outline-none border-0"
            style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)" }}
          >
            {t("cancel")}
          </motion.button>
          <motion.button whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }} onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 rounded-xl text-sm font-bold text-white cursor-pointer outline-none border-0"
            style={{ background: "linear-gradient(135deg, #fb923c, #ea580c)", boxShadow: "0 4px 16px rgba(251,146,60,0.35)", opacity: saving ? 0.55 : 1, transition: "opacity 0.2s" }}
          >
            {saving ? t("saving") : merchant ? t("updateMerchant") : t("createMerchantBtn")}
          </motion.button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FField = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

const mkInp = (extraStyle = {}) => ({
  width: "100%", padding: "9px 12px", borderRadius: 10, fontSize: 13,
  background: "rgba(2,6,23,0.7)", border: "1px solid rgba(148,163,184,0.1)",
  color: "#e2e8f0", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s",
  ...extraStyle,
});

const focusInp = e => {
  e.target.style.borderColor = "rgba(34,211,238,0.3)";
  e.target.style.boxShadow = "0 0 0 3px rgba(34,211,238,0.07)";
};

const blurInp = e => {
  e.target.style.borderColor = "rgba(148,163,184,0.1)";
  e.target.style.boxShadow = "none";
};